import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { auth, db } from "../../components/firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FcGoogle } from "react-icons/fc";
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { motion } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";
import { cn } from "../../lib/utils";
import logo from "../../assets/LOGO SnapED.png";

// Modern Input Component
const InputField = ({ label, icon: Icon, type, name, value, onChange, placeholder, isPassword, onForgot, showPassword, togglePassword }) => (
  <div className="flex flex-col gap-2">
    <label className="text-[13px] font-semibold text-muted-foreground ml-1 flex justify-between">
      {label}
      {isPassword && onForgot && (
        <button type="button" onClick={onForgot} className="bg-none border-none text-primary text-xs font-bold cursor-pointer hover:underline">
          Forgot?
        </button>
      )}
    </label>
    <div className="relative group">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-lg transition-colors group-focus-within:text-primary" />
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="w-full pl-12 pr-4 py-3.5 bg-card border border-border rounded-2xl text-foreground text-sm outline-none transition-all focus:border-primary focus:ring-4 focus:ring-primary/10"
      />
      {isPassword && (
        <button
          type="button"
          onClick={togglePassword}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-none border-none text-muted-foreground cursor-pointer flex hover:text-foreground transition-colors"
        >
          {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
        </button>
      )}
    </div>
  </div>
);

function Login({ isModal = false, onToggleMode }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    showPassword: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const googleProvider = new GoogleAuthProvider();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setUnverifiedUser(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        setUnverifiedUser(user);
        await signOut(auth);
        toast.error("Please verify your email before logging in.", { position: "top-center" });
        setIsLoading(false);
        return;
      }

      const userDoc = await getDoc(doc(db, "Users", user.uid));
      const role = userDoc.exists() ? userDoc.data().role : "user";

      toast.success("Welcome back!", { position: "top-center" });
      const from = location.state?.from;
      const redirectPath = from || (role === "company" ? "/job-portal/company/dashboard" : "/home");
      setTimeout(() => navigate(redirectPath), 1000);
    } catch (error) {
      let errorMessage = "Login failed. Please check your credentials.";
      if (error.code === "auth/user-not-found") errorMessage = "No account found with this email.";
      else if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") errorMessage = "Incorrect email or password.";
      toast.error(errorMessage, { position: "top-center" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser) {
      try {
        setIsLoading(true);
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        await sendEmailVerification(userCredential.user);
        await signOut(auth);
        toast.success("Verification email resent!");
      } catch {
        toast.error("Failed to resend. Try again later.");
      } finally {
        setIsLoading(false);
      }
      return;
    }
    try {
      setIsLoading(true);
      await sendEmailVerification(unverifiedUser);
      toast.success("Verification email resent!");
    } catch {
      toast.error("Failed to resend.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      toast.error("Enter your email first", { position: "top-center" });
      return;
    }
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, formData.email);
      toast.success("Reset link sent! Check your inbox.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const userRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userRef);
      let role = "user";

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          fullName: user.displayName || "Google User",
          role: "user",
          createdAt: serverTimestamp(),
          provider: "google"
        });
      } else {
        role = userDoc.data().role || "user";
      }

      toast.success("Signed in with Google!");
      const from = location.state?.from;
      const redirectPath = from || (role === "company" ? "/job-portal/company/dashboard" : "/home");
      setTimeout(() => navigate(redirectPath), 1000);
    } catch (error) {
      toast.error("Google sign in failed.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn(
      "min-h-screen flex items-center justify-center p-5 relative overflow-hidden font-sans transition-colors duration-300",
      isModal ? "min-h-auto bg-transparent p-0" : "bg-background"
    )}>
      {/* Background elements - hidden if modal */}
      {!isModal && (
        <>
          <div className="absolute inset-0 z-0">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, 30, 0]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute -top-[10%] -left-[10%] w-[60%] h-[60%] bg-primary/10 blur-[120px] rounded-full opacity-30"
            />
            <motion.div
              animate={{
                scale: [1.2, 1, 1.2],
                x: [0, -40, 0],
                y: [0, -50, 0]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-[10%] -right-[10%] w-[70%] h-[70%] bg-primary/10 blur-[120px] rounded-full opacity-20"
            />
          </div>

          {/* Back Button */}
          <motion.button
            onClick={() => navigate('/')}
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
            className="absolute top-8 left-8 flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-foreground text-sm font-semibold cursor-pointer z-10 backdrop-blur-md hover:bg-accent transition-colors shadow-sm"
          >
            <FiArrowLeft /> Back to Home
          </motion.button>
        </>
      )}

      <motion.div
        initial={isModal ? {} : { opacity: 0, scale: 0.95 }}
        animate={isModal ? {} : { opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-[440px] z-10"
      >
        <div className="text-center mb-8">
          {!isModal && (
            <Link to="/">
              <motion.img
                whileHover={{ scale: 1.05 }}
                src={logo} alt="SnapED" className="h-10 mx-auto mb-6 drop-shadow-[0_0_15px_rgba(34,197,94,0.2)]"
              />
            </Link>
          )}

          <h1 className="text-3xl font-black text-foreground mb-2 tracking-tight">
            Welcome Back
          </h1>
          <p className="text-muted-foreground text-[15px] font-medium opacity-80">
            Login to your SnapED account
          </p>
        </div>

        <div className="bg-card/50 backdrop-blur-2xl rounded-[2rem] p-8 border border-border/60 shadow-2xl">
          <form onSubmit={handleEmailLogin} className="flex flex-col gap-5">
            <InputField
              label="Email Address"
              icon={FiMail}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="name@example.com"
            />

            <InputField
              label="Password"
              icon={FiLock}
              type={formData.showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              isPassword
              onForgot={handleForgotPassword}
              showPassword={formData.showPassword}
              togglePassword={() => setFormData(p => ({ ...p, showPassword: !p.showPassword }))}
            />

            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-2 py-4 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground text-[15px] font-black border-none cursor-pointer flex items-center justify-center gap-2.5 shadow-xl shadow-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Authenticating..." : <>Sign In <FiArrowRight size={18} /></>}
            </motion.button>

            {unverifiedUser && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/20 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-xs text-amber-500 font-bold">Email not verified.</span>
                </div>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  className="bg-amber-500 border-none text-black rounded-lg px-3 py-1.5 text-xs font-black cursor-pointer hover:bg-amber-600 transition-colors"
                >
                  Resend Link
                </button>
              </motion.div>
            )}

            <div className="flex items-center gap-4 my-2">
              <div className="flex-1 h-px bg-border/60" />
              <span className="text-muted-foreground text-[10px] font-black uppercase tracking-[0.2em] opacity-60">OR</span>
              <div className="flex-1 h-px bg-border/60" />
            </div>

            <motion.button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="py-4 rounded-2xl bg-background/50 border border-border text-foreground text-[15px] font-bold cursor-pointer flex items-center justify-center gap-3 transition-all hover:bg-background hover:border-primary/40 disabled:opacity-50"
            >
              <FcGoogle size={22} /> Continue with Google
            </motion.button>
          </form>
        </div>

        <div className={cn(
          "text-center mt-8 p-6 rounded-2xl border transition-all duration-300",
          isModal ? "bg-card border-border" : "border-transparent"
        )}>
          <p className="text-sm text-muted-foreground font-medium m-0">
            {isModal ? "Don't have an account yet?" : "New to SnapED?"}{" "}
            {isModal ? (
              <button
                onClick={onToggleMode}
                className="text-primary font-black cursor-pointer text-[15px] px-2 py-1 rounded-lg hover:bg-primary/10 transition-all transition-colors"
              >
                Sign Up Now
              </button>
            ) : (
              <Link to="/signup" className="text-primary font-black hover:underline">Create Account</Link>
            )}
          </p>
        </div>

        {/* Simple Footer Links */}
        <div className="mt-8 text-center flex justify-center gap-5">
          <Link to="/privacy" className="text-muted-foreground text-xs font-bold hover:text-foreground hover:underline transition-colors uppercase tracking-widest opacity-60">Privacy Policy</Link>
          <Link to="/terms" className="text-muted-foreground text-xs font-bold hover:text-foreground hover:underline transition-colors uppercase tracking-widest opacity-60">Terms of Service</Link>
        </div>
      </motion.div>
      <ToastContainer theme={isDarkMode ? "dark" : "light"} position="top-center" limit={1} />
    </div>
  );
}

export default Login;